import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import { createSla, removeSla, slaOperationsQuery, slaSubcategoriesQuery } from '../../api/settingsEaseedeskSla'
import {
  changeGroupDispatch,
  changeTypeCategory,
  changeTypeModule,
  SHEET_PANES,
  simulateSla,
  ticketTypeSheetQuery,
  updateCatalogEntry,
  type SheetPane,
  type SlaSimulation,
  type TicketTypeSheetPage as SheetPage,
} from '../../api/settingsEaseedeskTicketTypeSheet'
import { chooseTicketTypePriority, chooseTicketTypeService, saveTicketTypeTimer, setTicketTypeDefaultPriority } from '../../api/settingsEaseedeskTicketTypes'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { failed, useSheetMutation } from './sheetHooks'
import { FlowPane } from './TicketTypeFlowPane'

/**
 * Fisa unui tip de tichet, pe file, dupa
 * `easeedesk_settings/tickets/ticket_types/overview/index.php`: ce e tipul,
 * fluxul lui (cu constructorul de statusuri si pasi), timpul si prioritatea
 * (cu simulatorul de SLA), cine e anuntat, categoriile si serviciile,
 * automatizarile, publicarea in catalog si istoricul schimbarilor.
 *
 * Adresa e a portalului: `/settings/easeedesk/ticket_type_overview/12[/flow]`.
 * Fiecare schimbare recititeste fisa, ca in portal (`reloadSheet`).
 */
const PANES: SheetPane[] = SHEET_PANES.map((p) => p.key)

function isPane(value: string | undefined): value is SheetPane {
  return value !== undefined && (PANES as string[]).includes(value)
}

export function TicketTypeSheetPage() {
  const params = useParams({ strict: false }) as { type?: string; pane?: string }
  const typeId = Number(params.type ?? 0)
  const pane: SheetPane = isPane(params.pane) ? params.pane : 'overview'
  const navigate = useNavigate()
  const page = useQuery(ticketTypeSheetQuery(typeId, pane))

  if (page.isError) {
    const status = page.error instanceof ApiError ? page.error.status : 0
    return (
      <div className="col-md-12">
        <p className="color-danger" style={{ marginTop: 20 }}>
          {status === 404 ? 'This ticket type does not belong to this service provider.' : failed(page.error)}
        </p>
      </div>
    )
  }
  if (!page.data) return <Spinner />
  const data = page.data
  const missing = data.checks.filter((c) => !c.ok).length

  const goTo = (next: SheetPane) => {
    void navigate({ to: '/settings/easeedesk/ticket_type_overview/$type/$pane', params: { type: String(typeId), pane: next } })
  }

  return (
    <div className="col-md-12 type-sheet" data-type={typeId}>
      <div className="row" style={{ marginTop: 10 }}>
        <div className="col-md-8">
          <h4 style={{ marginBottom: 4 }}>
            <span
              className="label"
              style={{ background: data.type.color_identifier || '#777', marginRight: 8, fontSize: 12 }}
              data-testid="sheet-short-name"
            >
              {data.type.short_name}
            </span>
            <strong data-testid="sheet-type-name">{data.type.type_name}</strong>
            {data.type.active === 1 ? (
              <span className="label label-success" style={{ marginLeft: 8 }}>
                active
              </span>
            ) : (
              <span className="label label-default" style={{ marginLeft: 8 }}>
                inactive
              </span>
            )}
            {data.type.never_used && (
              <span className="color-light-grey" style={{ fontSize: 12, marginLeft: 8 }}>
                never used on this instance
              </span>
            )}
          </h4>
          {data.type.description && <p className="color-light-grey">{data.type.description}</p>}
        </div>
        <div className="col-md-4 text-right" style={{ paddingTop: 12 }}>
          {missing > 0 ? (
            <span className="color-danger" data-testid="sheet-missing">
              <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> {missing} thing{missing === 1 ? '' : 's'} still missing
            </span>
          ) : (
            <span className="color-success">
              <i className="fas fa-check" aria-hidden="true"></i> ready to take tickets
            </span>
          )}
          <Link to="/settings/easeedesk/ticket_types_settings" className="btn btn-default btn-xs" style={{ marginLeft: 12 }}>
            <i className="fas fa-arrow-left" aria-hidden="true"></i> all types
          </Link>
        </div>
      </div>

      <ul className="nav nav-tabs type-sheet-tabs" role="tablist" style={{ marginBottom: 16 }}>
        {SHEET_PANES.map((p) => (
          <li key={p.key} role="presentation" className={p.key === pane ? 'active' : ''}>
            <a href={`#pane-${p.key}`} role="tab" data-pane={p.key} onClick={(e) => (e.preventDefault(), goTo(p.key))}>
              {p.label}
            </a>
          </li>
        ))}
      </ul>

      <div className="type-sheet-pane" data-pane={pane}>
        {pane === 'overview' && <OverviewPane data={data} goTo={goTo} />}
        {pane === 'flow' && <FlowPane data={data} />}
        {pane === 'time' && <TimePane data={data} />}
        {pane === 'notifications' && <NotificationsPane data={data} />}
        {pane === 'categories' && <CategoriesPane data={data} />}
        {pane === 'automations' && <AutomationsPane data={data} />}
        {pane === 'publishing' && <PublishingPane data={data} />}
        {pane === 'history' && <HistoryPane data={data} />}
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- overview */

function OverviewPane({ data, goTo }: { data: SheetPage; goTo: (p: SheetPane) => void }) {
  const cards: { label: string; value: ReactNode; pane: SheetPane }[] = [
    { label: 'Workflow', value: data.workflow ? `${data.workflow.workflow_name} (${data.workflow.steps} steps)` : 'none', pane: 'flow' },
    { label: 'Statuses', value: data.statuses.length, pane: 'flow' },
    { label: 'Forms', value: `${data.forms.own} own, ${data.forms.shared} shared`, pane: 'flow' },
    { label: 'Starting priority', value: data.priority ? data.priority.priority_name : 'none', pane: 'time' },
    { label: 'SLA rules', value: data.counts.sla, pane: 'time' },
    { label: 'Services', value: data.counts.services, pane: 'categories' },
    { label: 'Automations', value: data.counts.auto_status + data.counts.reopen + data.counts.feedback, pane: 'automations' },
    { label: 'Catalog entries', value: data.counts.catalog, pane: 'publishing' },
  ]
  return (
    <div className="row">
      <div className="col-md-7">
        <h5>
          <strong>What is in place</strong>
        </h5>
        <div className="row">
          {cards.map((card) => (
            <div className="col-md-6" key={card.label}>
              <div className="panel panel-default" style={{ marginBottom: 10, cursor: 'pointer' }} onClick={() => goTo(card.pane)}>
                <div className="panel-body" style={{ padding: '8px 12px' }}>
                  <span className="color-light-grey" style={{ fontSize: 11.5 }}>
                    {card.label}
                  </span>
                  <div>
                    <strong>{card.value}</strong>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="col-md-5">
        <h5>
          <strong>Checklist</strong>
        </h5>
        <ul className="list-unstyled sheet-checks">
          {data.checks.map((check) => (
            <li key={check.label} style={{ padding: '4px 0' }} className={check.ok ? '' : 'color-danger'}>
              <i className={`fas ${check.ok ? 'fa-check color-success' : 'fa-times'}`} aria-hidden="true" style={{ width: 18 }}></i>
              {check.label}{' '}
              {!check.ok && (
                <a href="#" onClick={(e) => (e.preventDefault(), goTo(check.pane))} style={{ fontSize: 11.5 }}>
                  {check.hint}
                </a>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------- time */

function TimePane({ data }: { data: SheetPage }) {
  const typeId = data.type.ID
  const start = useSheetMutation(typeId, (p: number) => setTicketTypeDefaultPriority(typeId, p))
  const allow = useSheetMutation(typeId, (v: { priority: number; on: boolean }) => chooseTicketTypePriority(typeId, v.priority, v.on))
  const timer = useSheetMutation(typeId, (v: { category: 1 | 2 | 3; time: number }) => saveTicketTypeTimer(typeId, v.category, v.time))
  const dropSla = useSheetMutation(typeId, (sla: number) => removeSla(sla))
  const [timers, setTimers] = useState({ take_over: data.timers?.take_over ?? 0, identify: data.timers?.identify ?? 0, intervention: data.timers?.intervention ?? 0 })
  const [sla, setSla] = useState({ priority: 0, category: 0, subcategory: 0, operation: 0, minutes: 0 })
  const subs = useQuery(slaSubcategoriesQuery(sla.category))
  const ops = useQuery(slaOperationsQuery(sla.subcategory))
  const addSla = useSheetMutation(
    typeId,
    () =>
      createSla({
        ci_id: 0,
        company_id: 0,
        type_id: typeId,
        category_id: sla.category,
        subcategory_id: sla.subcategory,
        operation_id: sla.operation,
        priority_id: sla.priority,
        sla_duration: sla.minutes,
      }),
    () => setSla({ priority: 0, category: 0, subcategory: 0, operation: 0, minutes: 0 }),
  )
  const [sim, setSim] = useState({ priority: 0, category: 0 })
  const [result, setResult] = useState<SlaSimulation | null>(null)
  const run = useMutation({
    mutationFn: () => simulateSla(typeId, sim.priority, sim.category),
    onSuccess: setResult,
    onError: (e) => toast(failed(e), 'Error', 'error'),
  })

  const timerKinds: { key: 'take_over' | 'identify' | 'intervention'; category: 1 | 2 | 3; label: string }[] = [
    { key: 'take_over', category: 1, label: 'Take over (minutes)' },
    { key: 'identify', category: 2, label: 'Identify (minutes)' },
    { key: 'intervention', category: 3, label: 'Intervention (minutes)' },
  ]

  return (
    <div>
      <div className="row">
        <div className="col-md-4">
          <h5>
            <strong>Priority</strong> a ticket starts with
          </h5>
          <select id="sheet-start-priority" className="form-control input-sm" value={data.priority?.ID ?? 0} onChange={(e) => start.mutate(Number(e.target.value))}>
            <option value={0}>- none, the agent picks -</option>
            {data.all_priorities.map((p) => (
              <option key={p.ID} value={p.ID}>
                {p.priority_name}
              </option>
            ))}
          </select>
          <h5>
            <strong>Priorities</strong> allowed on this type
          </h5>
          <p className="color-light-grey" style={{ fontSize: 11.5 }}>
            None ticked means all of them.
          </p>
          {data.all_priorities.map((p) => (
            <label key={p.ID} style={{ display: 'block', fontWeight: 'normal' }}>
              <input type="checkbox" className="sheet-allow-priority" data-priority={p.ID} checked={data.chosen_priorities.includes(p.ID)} onChange={(e) => allow.mutate({ priority: p.ID, on: e.target.checked })} />{' '}
              {p.priority_name}
            </label>
          ))}
        </div>
        <div className="col-md-4">
          <h5>
            <strong>Timers</strong>
          </h5>
          {timerKinds.map((t) => (
            <div className="form-group" key={t.key}>
              <label style={{ fontWeight: 'normal', fontSize: 11.5 }}>{t.label}</label>
              <input
                type="number"
                min={0}
                className="form-control input-sm sheet-timer"
                data-timer={t.key}
                value={timers[t.key]}
                onChange={(e) => setTimers({ ...timers, [t.key]: Number(e.target.value) })}
                onBlur={() => timer.mutate({ category: t.category, time: timers[t.key] })}
              />
            </div>
          ))}
        </div>
        <div className="col-md-4">
          <h5>
            <strong>SLA simulator</strong>
          </h5>
          <p className="color-light-grey" style={{ fontSize: 11.5 }}>
            Which rule catches a ticket of this type, and how long it gets.
          </p>
          <select id="sim-priority" className="form-control input-sm" value={sim.priority} onChange={(e) => setSim({ ...sim, priority: Number(e.target.value) })} style={{ marginBottom: 6 }}>
            <option value={0}>any priority</option>
            {data.sim_priorities.map((p) => (
              <option key={p.ID} value={p.ID}>
                {p.priority_name}
              </option>
            ))}
          </select>
          <select id="sim-category" className="form-control input-sm" value={sim.category} onChange={(e) => setSim({ ...sim, category: Number(e.target.value) })} style={{ marginBottom: 6 }}>
            <option value={0}>any category</option>
            {data.sim_categories.map((c) => (
              <option key={c.ID} value={c.ID}>
                {c.category_name}
              </option>
            ))}
          </select>
          <button type="button" id="sim-run" className="btn btn-default btn-sm" disabled={run.isPending} onClick={() => run.mutate()}>
            <i className="fas fa-play" aria-hidden="true"></i> simulate
          </button>
          {result && (
            <div id="sim-result" style={{ marginTop: 10, fontSize: 12 }}>
              {result.minutes === null ? (
                <span className="color-danger">No SLA rule catches this ticket: it gets no deadline.</span>
              ) : (
                <>
                  <strong>{result.minutes} minutes</strong>
                  {result.rule && (
                    <div className="color-light-grey">
                      from the rule on {result.rule.type}, {result.rule.category}, {result.rule.subcategory}, {result.rule.operation}, {result.rule.priority}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <h5 style={{ marginTop: 18 }}>
        <strong>SLA targets</strong> written for this type
      </h5>
      <table className="table table-hover sheet-sla">
        <thead>
          <tr>
            <th>Priority</th>
            <th>Category</th>
            <th>Subcategory</th>
            <th>Operation</th>
            <th>Minutes</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.sla_rules.map((r) => (
            <tr key={r.ID} data-sla={r.ID}>
              <td>{r.priority_name ?? 'any'}</td>
              <td>{r.category_name ?? 'any'}</td>
              <td>{r.subcategory_name ?? 'any'}</td>
              <td>{r.operation_name ?? 'any'}</td>
              <td>{r.sla}</td>
              <td className="text-right">
                <a href="#" className="color-danger sheet-sla-remove" onClick={(e) => (e.preventDefault(), dropSla.mutate(r.ID))}>
                  remove
                </a>
              </td>
            </tr>
          ))}
          {data.sla_rules.length === 0 && (
            <tr>
              <td colSpan={6} className="color-light-grey">
                No SLA rule names this type; the general ones apply, if any.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <form
        id="sheet-new-sla"
        className="form-inline"
        onSubmit={(e) => {
          e.preventDefault()
          if (sla.minutes > 0) addSla.mutate()
        }}
      >
        <select className="form-control input-sm" name="priority_id" value={sla.priority} onChange={(e) => setSla({ ...sla, priority: Number(e.target.value) })}>
          <option value={0}>any priority</option>
          {data.all_priorities.map((p) => (
            <option key={p.ID} value={p.ID}>
              {p.priority_name}
            </option>
          ))}
        </select>{' '}
        <select className="form-control input-sm" name="category_id" value={sla.category} onChange={(e) => setSla({ ...sla, category: Number(e.target.value), subcategory: 0, operation: 0 })}>
          <option value={0}>any category</option>
          {data.all_categories.map((c) => (
            <option key={c.ID} value={c.ID}>
              {c.category_name}
            </option>
          ))}
        </select>{' '}
        <select className="form-control input-sm" name="subcategory_id" value={sla.subcategory} disabled={sla.category === 0} onChange={(e) => setSla({ ...sla, subcategory: Number(e.target.value), operation: 0 })}>
          <option value={0}>any subcategory</option>
          {(subs.data?.subcategories ?? []).map((s) => (
            <option key={s.ID} value={s.ID}>
              {s.subcategory_name}
            </option>
          ))}
        </select>{' '}
        <select className="form-control input-sm" name="operation_id" value={sla.operation} disabled={sla.subcategory === 0} onChange={(e) => setSla({ ...sla, operation: Number(e.target.value) })}>
          <option value={0}>any operation</option>
          {(ops.data?.operations ?? []).map((o) => (
            <option key={o.ID} value={o.ID}>
              {o.operation_name}
            </option>
          ))}
        </select>{' '}
        <input type="number" min={1} className="form-control input-sm" name="sla_duration" placeholder="minutes" value={sla.minutes || ''} onChange={(e) => setSla({ ...sla, minutes: Number(e.target.value) })} style={{ width: 100 }} />{' '}
        <button type="submit" className="btn btn-primary btn-sm" disabled={addSla.isPending}>
          <i className="fas fa-plus" aria-hidden="true"></i> add target
        </button>
      </form>
    </div>
  )
}

/* ----------------------------------------------------------- notifications */

function NotificationsPane({ data }: { data: SheetPage }) {
  const events = data.notification_events
  const speaking = events.filter((e) => e.notifications > 0)
  const notices = speaking.reduce((n, e) => n + e.notifications, 0)
  if (events.length === 0) return <p className="color-light-grey">Notification events are not set up on this instance.</p>
  return (
    <div>
      <h5>
        <strong>Who gets told</strong>, and when
      </h5>
      <p className="color-light-grey" style={{ fontSize: 11.5 }}>
        {speaking.length ? (
          <>
            {notices} notification{notices === 1 ? '' : 's'} on {speaking.length} of the {events.length} events a ticket can go through. The other {events.length - speaking.length} pass without telling anybody.
          </>
        ) : (
          <span className="color-danger">
            <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> Nothing is sent for this type, on any of the {events.length} events.
          </span>
        )}{' '}
        Notifications are written in <Link to="/settings/easeedesk/notifications_settings">Notifications</Link>.
      </p>
      <table className="table table-hover">
        <thead>
          <tr>
            <th>Event</th>
            <th style={{ width: 120 }}>Notifications</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.ID} className="notify-row" data-event={e.ID}>
              <td className={e.notifications > 0 ? '' : 'color-light-grey'} title={e.description ?? ''}>
                {e.event.charAt(0).toUpperCase() + e.event.slice(1)}
              </td>
              <td>{e.notifications > 0 ? <span className="label label-primary">{e.notifications}</span> : <span className="color-light-grey">nobody</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h5>
        <strong>Templates</strong> the notifications can send
      </h5>
      <p className="color-light-grey" style={{ fontSize: 11.5 }}>
        Shared by the whole instance. The wording is written in <Link to="/settings/easeedesk/email_templates_settings">Email templates</Link>.
      </p>
      {data.email_templates.length ? (
        <table className="table">
          <tbody>
            {data.email_templates.map((t) => (
              <tr key={t.ID}>
                <td style={{ width: '40%' }}>{t.template_name}</td>
                <td className="color-light-grey">{t.template_subject}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="color-danger" style={{ fontSize: 11.5 }}>
          This instance has no email templates, so a notification would have nothing to send.
        </p>
      )}
    </div>
  )
}

/* -------------------------------------------------------------- categories */

function CategoriesPane({ data }: { data: SheetPage }) {
  const typeId = data.type.ID
  const category = useSheetMutation(typeId, (v: { id: number; on: boolean }) => changeTypeCategory(typeId, v.id, v.on))
  const service = useSheetMutation(typeId, (v: { id: number; on: boolean }) => chooseTicketTypeService(typeId, v.id, v.on))
  return (
    <div className="row">
      <div className="col-md-6">
        <h5>
          <strong>Categories</strong> a ticket of this type can be filed under
        </h5>
        <p className="color-light-grey" style={{ fontSize: 11.5 }}>
          None ticked means all of them.
        </p>
        {data.all_categories.map((c) => (
          <label key={c.ID} style={{ display: 'block', fontWeight: 'normal' }}>
            <input type="checkbox" className="sheet-category" data-category={c.ID} checked={data.type_categories.includes(c.ID)} onChange={(e) => category.mutate({ id: c.ID, on: e.target.checked })} /> {c.category_name}
          </label>
        ))}
        {data.all_categories.length === 0 && <p className="color-light-grey">No categories on this instance.</p>}
      </div>
      <div className="col-md-6">
        <h5>
          <strong>Services</strong> the type belongs to
        </h5>
        {data.all_services.map((s) => (
          <label key={s.ID} style={{ display: 'block', fontWeight: 'normal' }}>
            <input type="checkbox" className="sheet-service" data-service={s.ID} checked={data.type_services.includes(s.ID)} onChange={(e) => service.mutate({ id: s.ID, on: e.target.checked })} /> {s.service_name}
          </label>
        ))}
        {data.all_services.length === 0 && <p className="color-light-grey">No services enabled on this instance.</p>}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- automations */

function AutomationsPane({ data }: { data: SheetPage }) {
  const typeId = data.type.ID
  const module = useSheetMutation(typeId, (v: { module: 'planning' | 'escalation'; on: boolean }) => changeTypeModule(typeId, v.module, v.on))
  const dispatch = useSheetMutation(typeId, (v: { group: number; on: boolean }) => changeGroupDispatch(typeId, v.group, v.on))
  return (
    <div>
      <div className="row">
        <div className="col-md-6">
          <h5>
            <strong>Modules</strong> this type takes part in
          </h5>
          <label style={{ display: 'block', fontWeight: 'normal' }}>
            <input type="checkbox" id="sheet-module-planning" checked={data.in_planning} onChange={(e) => module.mutate({ module: 'planning', on: e.target.checked })} /> Planning
          </label>
          <label style={{ display: 'block', fontWeight: 'normal' }}>
            <input type="checkbox" id="sheet-module-escalation" checked={data.in_escalation} onChange={(e) => module.mutate({ module: 'escalation', on: e.target.checked })} /> Escalation
          </label>
        </div>
        <div className="col-md-6">
          <h5>
            <strong>Groups</strong> that dispatch this type
          </h5>
          {data.groups.map((g) => (
            <label key={g.ID} style={{ display: 'block', fontWeight: 'normal' }}>
              <input type="checkbox" className="sheet-dispatch" data-group={g.ID} checked={(data.group_dispatch[String(g.ID)] ?? 0) === typeId} onChange={(e) => dispatch.mutate({ group: g.ID, on: e.target.checked })} />{' '}
              {g.department_name}
              {(data.group_dispatch[String(g.ID)] ?? 0) > 0 && data.group_dispatch[String(g.ID)] !== typeId && (
                <span className="color-light-grey" style={{ fontSize: 11 }}>
                  {' '}
                  (dispatches {data.type_names[String(data.group_dispatch[String(g.ID)])] ?? 'another type'})
                </span>
              )}
            </label>
          ))}
        </div>
      </div>
      <h5 style={{ marginTop: 18 }}>
        <strong>Rules</strong> that move the ticket by themselves
      </h5>
      <table className="table table-hover sheet-automations">
        <tbody>
          {data.automations.map((a) => (
            <tr key={`${a.kind}-${a.id}`} className={a.active ? '' : 'color-light-grey'}>
              <td style={{ width: 160 }}>{a.what}</td>
              <td>{a.detail}</td>
              <td style={{ width: 80 }}>{a.active ? <span className="label label-success">on</span> : <span className="label label-default">off</span>}</td>
            </tr>
          ))}
          {data.triggers.map((t) => (
            <tr key={`trigger-${t.id}`}>
              <td>On {t.event_type}</td>
              <td>moves to {t.status_name ?? '?'}</td>
              <td>{t.activated_at ? <span className="label label-success">on</span> : <span className="label label-default">off</span>}</td>
            </tr>
          ))}
          {data.event_triggers.map((t) => (
            <tr key={`action-${t.id}`}>
              <td>Reaching {t.status_name ?? '?'}</td>
              <td>
                {data.trigger_actions[t.action] ?? t.action}
                {t.company_name ? ` (${t.company_name})` : ''}
              </td>
              <td></td>
            </tr>
          ))}
          {data.automations.length + data.triggers.length + data.event_triggers.length === 0 && (
            <tr>
              <td className="color-light-grey">Nothing moves by itself on this type.</td>
            </tr>
          )}
        </tbody>
      </table>
      <p className="color-light-grey" style={{ fontSize: 11.5 }}>
        Written in <Link to="/settings/easeedesk/auto_change_status">Auto change status</Link>, <Link to="/settings/easeedesk/reopen_ticket">Reopen</Link>,{' '}
        <Link to="/settings/easeedesk/feedback_settings">Feedback</Link> and <Link to="/settings/easeedesk/ticket_status_change_trigger_actions">Status change triggers</Link>.
      </p>
    </div>
  )
}

/* -------------------------------------------------------------- publishing */

function PublishingPane({ data }: { data: SheetPage }) {
  const typeId = data.type.ID
  const save = useSheetMutation(typeId, (v: { entry: number; short: string; long: string; published: boolean }) => updateCatalogEntry(typeId, v.entry, v.short, v.long, v.published), () =>
    toast('Catalog entry saved.', 'Saved'),
  )
  if (data.catalog_items.length === 0) {
    return (
      <p className="color-light-grey">
        This type is not in the service catalog. Requests for it are raised from the ticket screen only. Entries are made in <Link to="/settings/catalog">Service Catalog</Link>.
      </p>
    )
  }
  return (
    <div>
      <h5>
        <strong>Catalog entries</strong> that raise this type
      </h5>
      {data.catalog_items.map((item) => (
        <CatalogEntryForm key={item.ID} item={item} busy={save.isPending} onSave={(v) => save.mutate({ entry: item.ID, ...v })} />
      ))}
    </div>
  )
}

function CatalogEntryForm({ item, busy, onSave }: { item: SheetPage['catalog_items'][number]; busy: boolean; onSave: (v: { short: string; long: string; published: boolean }) => void }) {
  const [short, setShort] = useState(item.short_description)
  const [long, setLong] = useState(item.long_description ?? '')
  const [published, setPublished] = useState(item.published === 1)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    onSave({ short, long, published })
  }
  return (
    <form className="panel panel-default sheet-catalog-entry" data-entry={item.ID} onSubmit={submit} style={{ padding: 12, marginBottom: 10 }}>
      <div className="form-group">
        <input type="text" className="form-control input-sm" name="short_description" value={short} onChange={(e) => setShort(e.target.value)} placeholder="Name of the service" />
      </div>
      <div className="form-group">
        <textarea className="form-control input-sm" name="long_description" rows={2} value={long} onChange={(e) => setLong(e.target.value)} placeholder="What it is" />
      </div>
      <label style={{ fontWeight: 'normal' }}>
        <input type="checkbox" name="published" checked={published} onChange={(e) => setPublished(e.target.checked)} /> published
      </label>{' '}
      <button type="submit" className="btn btn-primary btn-sm" disabled={busy} style={{ marginLeft: 10 }}>
        save
      </button>
    </form>
  )
}

/* ----------------------------------------------------------------- history */

function HistoryPane({ data }: { data: SheetPage }) {
  if (data.history.length === 0) return <p className="color-light-grey">No settings change was recorded on this type yet.</p>
  return (
    <table className="table table-hover sheet-history">
      <thead>
        <tr>
          <th style={{ width: 150 }}>When</th>
          <th style={{ width: 160 }}>Who</th>
          <th>What</th>
        </tr>
      </thead>
      <tbody>
        {data.history.map((row) => (
          <tr key={row.ID}>
            <td>{row.created_at}</td>
            <td>{row.user_name || <em className="color-light-grey">system</em>}</td>
            <td>
              {row.action_label} {row.entity_label}
              {row.field_label ? <> - {row.field_label}</> : null}
              {row.old_label ? (
                <>
                  {' '}
                  {row.old_word} <Swatch color={row.old_color} /> {row.old_label}
                </>
              ) : null}
              {row.new_label ? (
                <>
                  {' '}
                  {row.new_word} <Swatch color={row.new_color} /> {row.new_label}
                </>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Swatch({ color }: { color: string | null }) {
  if (!color) return null
  return <span style={{ display: 'inline-block', width: 10, height: 10, background: color, borderRadius: 2, verticalAlign: 'middle' }} />
}
